import React from 'react'
import ReactSelect, {
  components as libComponents,
  ControlProps,
  StylesConfig,
  CSSObjectWithLabel,
  OptionProps,
  DropdownIndicatorProps,
} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import AsyncSelect from 'react-select/async'
import { COLORS, BOXSHADOWS, fontSizes, rem, lineHeights } from '../Theme'
import { ArrowIconActive, ArrowIconInactive } from '../Input/Icons'
import { ISelectProps } from './Select.types'
import { Checkbox } from 'Input/variations/Checkbox'
import { HelpText, Label, Required, Root } from 'Input/Input'
import css from './Select.module.scss'

const ArrowActive = (props: DropdownIndicatorProps) => (
  <libComponents.DropdownIndicator {...props}>
    <ArrowIconActive />
  </libComponents.DropdownIndicator>
)
const ArrowInactive = (props: DropdownIndicatorProps) => (
  <libComponents.DropdownIndicator {...props}>
    <ArrowIconInactive />
  </libComponents.DropdownIndicator>
)

const Option = (props: OptionProps) => {
  return (
    <div>
      <libComponents.Option {...props}>
        <div className={css.flex}>
          <Checkbox
            removeInput
            checked={props.isSelected}
            onChange={() => null}
          />{' '}
          <div className={css.text}>{props.label}</div>
        </div>
      </libComponents.Option>
    </div>
  )
}

const customStyles: StylesConfig = {
  //@ts-ignore
  input: (styles, { selectProps: { isDark } }) => ({
    ...styles,
    padding: 0,
    margin: 0,
    color: isDark ? COLORS.WHITE : undefined,
  }),
  control: (
    styles: CSSObjectWithLabel,
    {
      isDisabled,
      isFocused,
      //@ts-ignore
      selectProps: { isDark, isInvalid },
    }: ControlProps
  ) => ({
    ...styles,
    border: `1px solid ${
      isDisabled
        ? COLORS.BG_DIVIDER
        : isInvalid
        ? COLORS.ERROR_DEFAULT
        : isDark
        ? COLORS.DARK_BG_LIGHT
        : COLORS.BG_DIVIDER
    }`,
    backgroundColor: isDark
      ? COLORS.DARK_BG_DEFAULT
      : isDisabled
      ? COLORS.BG_DISABLED
      : isInvalid
      ? COLORS.ERROR_LIGHT
      : COLORS.WHITE,
    '&:hover': {
      border: `1px solid ${
        isDark ? COLORS.DARK_DIVIDER : COLORS.BG_DARK_DIVIDER
      }`,
    },
    padding: `${rem(8)} ${rem(16)}`,
    paddingRight: rem(9),
    fontSize: fontSizes.m,
    lineHeight: lineHeights.m,
    boxShadow: 'none',
    outline: isFocused ? `2px solid ${isDark ? '#ffffff' : '#96c0ff'}` : 'none',
    outlineOffset: '2px',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
  }),
  placeholder: (
    styles,
    {
      isDisabled,
      //@ts-ignore
      selectProps: { isDark },
    }
  ) => ({
    ...styles,
    fontSize: fontSizes.m,
    color: isDark
      ? COLORS.DARK_TXT_PLACEHOLDER
      : isDisabled
      ? COLORS.TXT_DISABLED
      : COLORS.TXT_PLACEHOLDER,
    margin: 0,
    textAlign: 'left',
  }),
  //@ts-ignore
  menu: (styles, { selectProps: { isDark } }) => ({
    ...styles,
    borderRadius: '8px',
    color: isDark ? COLORS.DARK_TXT_DEFAULT : COLORS.TXT_DEFAULT,
    overflow: 'auto',
    boxShadow: BOXSHADOWS.LEVEL_1,
    zIndex: 30,
    padding: `${rem(8)} 0`,
    backgroundColor: isDark ? COLORS.DARK_BG_DEFAULT : COLORS.WHITE,
  }),
  option: (
    styles: CSSObjectWithLabel,
    {
      isDisabled,
      //@ts-ignore
      selectProps: { isDark },
    }: OptionProps
  ) => ({
    ...styles,
    padding: `${rem(12)} ${rem(16)}`,
    fontSize: fontSizes.m,
    lineHeight: lineHeights.m,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    color: isDisabled ? COLORS.ERROR_DEFAULT : undefined,
    backgroundColor: isDark ? COLORS.DARK_BG_DEFAULT : COLORS.WHITE,
    ':hover': {
      ...styles[':active'],
      backgroundColor: isDark ? COLORS.DARK_BG_LIGHT : '#ebebeb',
    },
    ':active': {
      ...styles[':active'],
      backgroundColor: isDark ? COLORS.DARK_BG_DARK : '#e0e0e0',
    },
  }),
  //@ts-ignore
  singleValue: (styles, { isDisabled, selectProps: { isDark } }) => ({
    ...styles,
    width: '100%',
    textAlign: 'left',
    fontSize: fontSizes.m,
    color: isDark
      ? COLORS.WHITE
      : isDisabled
      ? COLORS.TXT_DISABLED
      : COLORS.TXT_DEFAULT,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (
    styles,
    {
      //@ts-ignore
      selectProps: { isDark },
    }
  ) => ({
    ...styles,
    height: rem(22),
    padding: rem(8),
    svg: {
      path: {
        fill: isDark ? COLORS.DARK_TXT_PLACEHOLDER : undefined,
      },
    },
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: '0',
  }),
}

const checkboxProps = {
  isMulti: true,
  closeMenuOnSelect: false,
  hideSelectedOptions: false,
  allowSelectAll: true,
  isClearable: false,
}

export const Select = ({
  components,
  disabled,
  helpText,
  invalid,
  isAsync,
  isBlock,
  isCheckbox,
  isDark,
  label,
  options,
  placeholder = 'Select',
  required,
  value,
  onChange,
  onCreateOption,
  onBlur,
  ...passingProps
}: ISelectProps) => {
  const SelectComponent = isAsync
    ? AsyncSelect
    : onCreateOption
    ? CreatableSelect
    : ReactSelect

  return (
    <Root isDark={isDark} $isBlock={isBlock}>
      {label && (
        <Label>
          {label}
          {required ? <Required /> : null}
        </Label>
      )}

      <SelectComponent
        value={value}
        options={options}
        onChange={onChange}
        styles={customStyles}
        components={{
          DropdownIndicator: disabled ? ArrowInactive : ArrowActive,
          Option: isCheckbox ? Option : libComponents?.Option,
          ...components,
        }}
        {...(isCheckbox ? checkboxProps : {})}
        menuPortalTarget={document.getElementById('select-menu')}
        placeholder={placeholder}
        isDisabled={disabled}
        //@ts-ignore
        isDark={isDark}
        //@ts-ignore
        isInvalid={invalid}
        onBlur={onBlur}
        onCreateOption={onCreateOption}
        {...passingProps}
      />
      {helpText && <HelpText>{helpText}</HelpText>}
    </Root>
  )
}
